"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { Link } from "@/components/link";

type ChildCompany = { id: string; name: string };

interface CompanyChildCompaniesProps {
  companyId: string;
  childCompanies: ChildCompany[];
}

/** Filterable list of related organizations, shown in the company About panel. */
export function CompanyChildCompanies({ companyId, childCompanies }: CompanyChildCompaniesProps) {
  const t = useTranslations("label");
  const [filterText, setFilterText] = useState("");
  const query = filterText.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      query
        ? childCompanies.filter((child) => child.name.toLowerCase().includes(query))
        : childCompanies,
    [childCompanies, query],
  );
  const compact = childCompanies.length > 24;

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`company-child-filter-${companyId}`} className="sr-only">
          {t("childLabelsFilterPlaceholder")}
        </label>
        <input
          id={`company-child-filter-${companyId}`}
          type="search"
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder={t("childLabelsFilterPlaceholder")}
          autoComplete="off"
          className="w-full max-sm:min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("childLabelsNoneMatch")}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5 md:gap-2">
          {filtered.map((child) => (
            <li key={child.id}>
              <Link
                to={`/company/${child.id}`}
                className={
                  compact
                    ? "inline-flex items-center rounded-md border border-border bg-secondary/40 px-2 py-0.5 text-xs text-foreground transition-colors hover:border-primary/30 hover:bg-secondary"
                    : "inline-flex items-center rounded-lg border border-border bg-secondary/40 px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary/30 hover:bg-secondary"
                }
              >
                {child.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
